/**
 * WebRTC P2P Service
 * Handles peer-to-peer connections for low-latency streaming
 */

import {
    WebRTCConfig,
    WebRTCSignal,
    WebRTCSignalType,
    WebRTCStats,
    IceCandidateInfo,
    FrameData,
    DataChannelMessageType,
    IceServersResponse,
    TurnServerConfig
} from '../types';
import { DataChannelProtocol } from '../utils/DataChannelProtocol';

export interface WebRTCServiceEvents {
    onFrameReceived?: (frame: FrameData) => void;
    onInputReceived?: (data: Uint8Array) => void;
    onAudioReceived?: (data: Uint8Array) => void;
    onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
    onDataChannelOpen?: () => void;
    onDataChannelClose?: () => void;
    onIceCandidate?: (candidate: IceCandidateInfo) => void;
    onLocalDescription?: (sdp: string, type: string) => void;
    onStatsUpdate?: (stats: WebRTCStats) => void;
    onLog?: (message: string) => void;
}

const DEFAULT_CONFIG: WebRTCConfig = {
    stunServers: [
        'stun:stun.l.google.com:19302',
        'stun:stun1.l.google.com:19302',
        'stun:stun2.l.google.com:19302'
    ],
    dataChannelBufferThreshold: 1024 * 1024, // 1MB
    connectionTimeoutMs: 30000
};

/**
 * Build WebRTCConfig from ICE servers response from server
 */
export function buildConfigFromIceServers(
    response: IceServersResponse,
    baseConfig: Partial<WebRTCConfig> = {}
): WebRTCConfig {
    const config: WebRTCConfig = {
        stunServers: [],
        dataChannelBufferThreshold: baseConfig.dataChannelBufferThreshold ?? DEFAULT_CONFIG.dataChannelBufferThreshold,
        connectionTimeoutMs: baseConfig.connectionTimeoutMs ?? DEFAULT_CONFIG.connectionTimeoutMs
    };

    for (const server of response.iceServers) {
        if (!server.username) {
            // STUN server (no credentials)
            config.stunServers.push(...server.urls);
        } else {
            // TURN server (with credentials)
            config.turnServer = {
                url: server.urls[0] || '',
                username: server.username,
                credential: server.credential || ''
            };
            console.log(`[WebRTC] TURN server configured: ${config.turnServer.url} (Provider: ${response.provider})`);
        }
    }

    console.log(`[WebRTC] Configured ${config.stunServers.length} STUN servers, TURN: ${config.turnServer ? 'Yes' : 'No'}`);
    return config;
}

export class WebRTCService {
    private peerConnection: RTCPeerConnection | null = null;
    private dataChannel: RTCDataChannel | null = null;
    private config: WebRTCConfig;
    private events: WebRTCServiceEvents;
    private isInitiator = false;
    private connectionTimeoutId: number | null = null;

    // Perfect Negotiation state (MDN pattern)
    // Client = impolite (wins collision), Agent = polite (yields)
    private polite = false; // WebClient is always the impolite side
    private makingOffer = false;
    private ignoreOffer = false;

    // BUG-2: ICE candidate queue
    private remoteDescriptionSet = false;
    private pendingCandidates: IceCandidateInfo[] = [];

    // Statistics
    private bytesSent = 0;
    private bytesReceived = 0;
    private framesSent = 0;
    private framesReceived = 0;
    private lastPingTime = 0;
    private roundTripTimeMs = 0;
    private pingIntervalId: number | null = null;

    constructor(config: Partial<WebRTCConfig> = {}, events: WebRTCServiceEvents = {}) {
        this.config = { ...DEFAULT_CONFIG, ...config };
        this.events = events;
    }

    /**
     * Initialize as the initiating peer (creates offer)
     * For web client, we typically initiate the connection
     */
    async initializeAsInitiator(): Promise<string> {
        this.isInitiator = true;
        this.polite = false; // Client = impolite (wins collision)
        await this.initializePeerConnection();

        // Create DataChannel (initiator creates it)
        this.dataChannel = this.peerConnection!.createDataChannel('remotedesktop', {
            ordered: false, // Unordered for lowest latency
            maxRetransmits: 0 // No retransmits for real-time data
        });

        this.setupDataChannelHandlers(this.dataChannel);
        this.log('DataChannel created (initiator)');

        // Create offer (Perfect Negotiation: track makingOffer state)
        this.makingOffer = true;
        try {
            const offer = await this.peerConnection!.createOffer();
            await this.peerConnection!.setLocalDescription(offer);
        } finally {
            this.makingOffer = false;
        }

        this.log(`Created SDP offer, ICE gathering state: ${this.peerConnection!.iceGatheringState}`);

        // Start connection timeout
        this.startConnectionTimeout();

        return this.peerConnection!.localDescription!.sdp;
    }

    /**
     * Initialize as the receiving peer (waits for offer)
     */
    async initializeAsReceiver(): Promise<void> {
        this.isInitiator = false;
        await this.initializePeerConnection();
        this.log('Initialized as receiver, waiting for DataChannel');
    }

    /**
     * Process remote SDP offer (receiver side).
     * Implements Perfect Negotiation collision handling.
     */
    async processOffer(sdpOffer: string): Promise<string | null> {
        if (!this.peerConnection) {
            throw new Error('Peer connection not initialized');
        }

        // Perfect Negotiation: detect collision
        const offerCollision = this.makingOffer ||
            this.peerConnection.signalingState !== 'stable';

        this.ignoreOffer = !this.polite && offerCollision;
        if (this.ignoreOffer) {
            this.log('Offer collision detected — impolite side ignoring incoming offer');
            return null;
        }

        // If polite and colliding, rollback our own offer first
        if (offerCollision) {
            this.log('Offer collision detected — polite side rolling back local offer');
        }

        const offer: RTCSessionDescriptionInit = {
            type: 'offer',
            sdp: sdpOffer
        };

        await this.peerConnection.setRemoteDescription(offer);
        this.log('Remote offer set successfully');
        await this.flushPendingCandidates(); // BUG-2

        // Create answer
        const answer = await this.peerConnection.createAnswer();
        await this.peerConnection.setLocalDescription(answer);

        // Reset ignoreOffer after successful negotiation
        this.ignoreOffer = false;

        this.log('Created SDP answer');
        return answer.sdp!;
    }

    /**
     * Process remote SDP answer (initiator side)
     */
    async processAnswer(sdpAnswer: string): Promise<void> {
        if (!this.peerConnection) {
            throw new Error('Peer connection not initialized');
        }

        const answer: RTCSessionDescriptionInit = {
            type: 'answer',
            sdp: sdpAnswer
        };

        await this.peerConnection.setRemoteDescription(answer);
        this.ignoreOffer = false; // Reset after successful negotiation
        this.log('Remote answer set successfully');
        await this.flushPendingCandidates(); // BUG-2
    }

    /**
     * Add remote ICE candidate (BUG-2: queues if remote description not yet set)
     */
    async addIceCandidate(candidateInfo: IceCandidateInfo): Promise<void> {
        if (!this.peerConnection) return;

        // Perfect Negotiation: skip candidates from ignored offers
        if (this.ignoreOffer) return;

        // BUG-2: Queue candidates until remote description is set
        if (!this.remoteDescriptionSet) {
            this.pendingCandidates.push(candidateInfo);
            this.log(`Queued ICE candidate (waiting for remote description)`);
            return;
        }

        const candidate: RTCIceCandidateInit = {
            candidate: candidateInfo.candidate,
            sdpMid: candidateInfo.sdpMid,
            sdpMLineIndex: candidateInfo.sdpMLineIndex ?? undefined
        };

        await this.peerConnection.addIceCandidate(candidate);
        this.log(`Added ICE candidate: ${candidateInfo.candidate.substring(0, 50)}...`);
    }

    private async flushPendingCandidates(): Promise<void> {
        this.remoteDescriptionSet = true;
        if (this.pendingCandidates.length > 0) {
            this.log(`Flushing ${this.pendingCandidates.length} queued ICE candidates`);
            const candidates = [...this.pendingCandidates];
            this.pendingCandidates = [];
            for (const c of candidates) {
                await this.addIceCandidate(c);
            }
        }
    }

    /**
     * Send input command via P2P
     */
    sendInput(inputJson: string): boolean {
        if (!this.isDataChannelOpen) {
            return false;
        }

        // HIGH-6: Backpressure check
        if (this.isBufferFull()) {
            this.log('DataChannel buffer full — dropping input');
            return false;
        }

        try {
            const message = DataChannelProtocol.createInputMessage(inputJson);
            // Send as ArrayBuffer - DataChannel.send() accepts ArrayBuffer
            this.dataChannel!.send(message.buffer.slice(message.byteOffset, message.byteOffset + message.byteLength) as ArrayBuffer);
            this.bytesSent += message.length;
            return true;
        } catch (error) {
            this.log(`Failed to send input: ${error}`);
            return false;
        }
    }

    /**
     * Send ping for latency measurement
     */
    sendPing(): void {
        if (!this.isDataChannelOpen) return;

        // HIGH-6: Skip ping if buffer overloaded
        if (this.isBufferFull()) return;

        try {
            this.lastPingTime = Date.now();
            const message = DataChannelProtocol.createPingMessage();
            this.dataChannel!.send(message.buffer.slice(message.byteOffset, message.byteOffset + message.byteLength) as ArrayBuffer);
        } catch (error) {
            this.log(`Failed to send ping: ${error}`);
        }
    }

    // HIGH-6: Backpressure — matches C# DataChannelBufferThreshold (1 MB)
    private isBufferFull(): boolean {
        if (!this.dataChannel) return true;
        return this.dataChannel.bufferedAmount > this.config.dataChannelBufferThreshold;
    }

    /**
     * Get connection statistics
     */
    getStats(): WebRTCStats {
        return {
            bytesSent: this.bytesSent,
            bytesReceived: this.bytesReceived,
            packetsSent: this.framesSent,
            packetsReceived: this.framesReceived,
            roundTripTimeMs: this.roundTripTimeMs,
            lastUpdated: new Date()
        };
    }

    /**
     * Close the connection
     */
    close(): void {
        this.stopConnectionTimeout();
        this.stopPingInterval();

        // BUG-2: Reset candidate queue
        this.pendingCandidates = [];
        this.remoteDescriptionSet = false;

        // Reset negotiation state
        this.makingOffer = false;
        this.ignoreOffer = false;

        // HIGH-3: Clear event handlers before closing to prevent callbacks on disposed objects
        if (this.dataChannel) {
            this.dataChannel.onopen = null;
            this.dataChannel.onclose = null;
            this.dataChannel.onmessage = null;
            this.dataChannel.onerror = null;
            this.dataChannel.close();
            this.dataChannel = null;
        }

        if (this.peerConnection) {
            this.peerConnection.onicecandidate = null;
            this.peerConnection.oniceconnectionstatechange = null;
            this.peerConnection.onicegatheringstatechange = null;
            this.peerConnection.ondatachannel = null;
            this.peerConnection.onconnectionstatechange = null;
            this.peerConnection.close();
            this.peerConnection = null;
        }

        this.isInitiator = false;
        this.log('WebRTC connection closed');
    }

    /**
     * Check if DataChannel is open
     */
    get isDataChannelOpen(): boolean {
        return this.dataChannel?.readyState === 'open';
    }

    /**
     * Get connection state
     */
    get connectionState(): RTCPeerConnectionState | null {
        return this.peerConnection?.connectionState ?? null;
    }

    /**
     * Get round-trip time
     */
    get rtt(): number {
        return this.roundTripTimeMs;
    }

    // =========================================================================
    // Private Methods
    // =========================================================================

    private async initializePeerConnection(): Promise<void> {
        // Configure ICE servers
        const iceServers: RTCIceServer[] = this.config.stunServers.map(url => ({ urls: url }));

        if (this.config.turnServer) {
            iceServers.push({
                urls: this.config.turnServer.url,
                username: this.config.turnServer.username,
                credential: this.config.turnServer.credential
            });
        }

        const rtcConfig: RTCConfiguration = {
            iceServers,
            iceCandidatePoolSize: 10
        };

        this.peerConnection = new RTCPeerConnection(rtcConfig);

        // Handle ICE candidates
        this.peerConnection.onicecandidate = (event) => {
            if (event.candidate) {
                const info: IceCandidateInfo = {
                    candidate: event.candidate.candidate,
                    sdpMid: event.candidate.sdpMid,
                    sdpMLineIndex: event.candidate.sdpMLineIndex
                };
                this.events.onIceCandidate?.(info);
                this.log(`ICE candidate generated: ${event.candidate.type}`);
            }
        };

        // Handle ICE gathering state changes
        this.peerConnection.onicegatheringstatechange = () => {
            this.log(`ICE gathering state: ${this.peerConnection?.iceGatheringState}`);
        };

        // Handle connection state changes
        this.peerConnection.onconnectionstatechange = () => {
            const state = this.peerConnection?.connectionState;
            this.log(`Connection state: ${state}`);

            if (state) {
                this.events.onConnectionStateChange?.(state);
            }

            if (state === 'connected') {
                this.stopConnectionTimeout();
                this.startPingInterval();
            } else if (state === 'failed' || state === 'closed') {
                this.stopPingInterval();
            }
        };

        // Handle ICE connection state changes
        this.peerConnection.oniceconnectionstatechange = () => {
            this.log(`ICE connection state: ${this.peerConnection?.iceConnectionState}`);
        };

        // Perfect Negotiation: onnegotiationneeded for renegotiation
        this.peerConnection.onnegotiationneeded = async () => {
            if (!this.isInitiator) return; // Only initiator creates offers
            try {
                this.makingOffer = true;
                const offer = await this.peerConnection!.createOffer();
                // Check if signaling state changed during async operation
                if (this.peerConnection!.signalingState !== 'stable') {
                    this.log('onnegotiationneeded: signaling state changed during offer creation, aborting');
                    return;
                }
                await this.peerConnection!.setLocalDescription(offer);
                this.events.onLocalDescription?.(
                    this.peerConnection!.localDescription!.sdp,
                    'offer'
                );
                this.log('onnegotiationneeded: created and set new offer');
            } catch (err) {
                this.log(`onnegotiationneeded error: ${err}`);
            } finally {
                this.makingOffer = false;
            }
        };

        // Handle incoming DataChannel (receiver side)
        this.peerConnection.ondatachannel = (event) => {
            this.log(`DataChannel received: ${event.channel.label}`);
            this.dataChannel = event.channel;
            this.setupDataChannelHandlers(event.channel);
        };

        this.log('Peer connection initialized');
    }

    private setupDataChannelHandlers(channel: RTCDataChannel): void {
        channel.binaryType = 'arraybuffer';

        channel.onopen = () => {
            this.log('DataChannel opened - ready for P2P communication!');
            this.events.onDataChannelOpen?.();
        };

        channel.onclose = () => {
            this.log('DataChannel closed');
            this.events.onDataChannelClose?.();
        };

        channel.onerror = (event) => {
            this.log(`DataChannel error: ${event}`);
        };

        channel.onmessage = (event) => {
            this.processReceivedData(event.data);
        };
    }

    private processReceivedData(data: ArrayBuffer): void {
        this.bytesReceived += data.byteLength;

        const parsed = DataChannelProtocol.parseMessage(data);
        if (!parsed) return;

        const { type, payload } = parsed;

        switch (type) {
            case DataChannelMessageType.Frame: {
                const frame = DataChannelProtocol.parseFrameMessage(payload);
                if (frame) {
                    this.framesReceived++;
                    this.events.onFrameReceived?.(frame);
                }
                break;
            }

            case DataChannelMessageType.Input:
                this.events.onInputReceived?.(payload);
                break;

            case DataChannelMessageType.Audio:
                this.events.onAudioReceived?.(payload);
                break;

            case DataChannelMessageType.Ping: {
                // Respond with pong
                const pongMessage = DataChannelProtocol.createPongMessage(payload);
                if (this.dataChannel?.readyState === 'open') {
                    this.dataChannel.send(pongMessage.buffer.slice(pongMessage.byteOffset, pongMessage.byteOffset + pongMessage.byteLength) as ArrayBuffer);
                }
                break;
            }

            case DataChannelMessageType.Pong: {
                // Calculate RTT
                this.roundTripTimeMs = DataChannelProtocol.parsePongMessage(payload);
                this.events.onStatsUpdate?.(this.getStats());
                break;
            }
        }
    }

    private startConnectionTimeout(): void {
        this.connectionTimeoutId = window.setTimeout(() => {
            if (!this.isDataChannelOpen) {
                this.log('Connection timeout - P2P failed');
                this.close();
            }
        }, this.config.connectionTimeoutMs);
    }

    private stopConnectionTimeout(): void {
        if (this.connectionTimeoutId !== null) {
            clearTimeout(this.connectionTimeoutId);
            this.connectionTimeoutId = null;
        }
    }

    private startPingInterval(): void {
        this.pingIntervalId = window.setInterval(() => {
            this.sendPing();
        }, 5000); // Ping every 5 seconds
    }

    private stopPingInterval(): void {
        if (this.pingIntervalId !== null) {
            clearInterval(this.pingIntervalId);
            this.pingIntervalId = null;
        }
    }

    private log(message: string): void {
        const timestamp = new Date().toISOString().substr(11, 12);
        const logMessage = `[${timestamp}] [WebRTC] ${message}`;
        console.log(logMessage);
        this.events.onLog?.(logMessage);
    }
}
